import { DateTime } from 'luxon';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';
import { DQCascadeItemStatus, DQCascadeRunResource } from '@/types/resources';
import { Color } from '@/utils/theme';

// Must match DQCascadeRun::groupName() on the backend.
const CASCADE_GROUP_NAME = /^DQ Cascade (\d+)$/i;

export const getCascadeGroupNumber = (groupName: string): number | null => {
  const match = groupName.trim().match(CASCADE_GROUP_NAME);

  return match ? Number(match[1]) : null;
};

export const dqCascadeQueryKeys = {
  all: ['dq_cascade'] as const,
  groups: () => [...dqCascadeQueryKeys.all, 'groups'] as const,
  runs: (filters: object) =>
    [...dqCascadeQueryKeys.all, 'runs', filters] as const,
  items: (filters: object) =>
    [...dqCascadeQueryKeys.all, 'items', filters] as const,
};

// Refresh lists every 10 seconds while the group still has items being sent.
export const RUNNING_REFETCH_INTERVAL = 10000;

export const formatDate = (date?: string | null) =>
  date ? DateTime.fromISO(date).toFormat('dd/LL/yyyy') : '-';

export const formatTime = (date?: string | null) =>
  date ? DateTime.fromISO(date).toFormat('HH:mm') : '';

/**
 * A run takes main-page posts published after `windowFrom` up to and
 * including `windowTo`.
 */
export const formatRunWindow = (
  run: Pick<DQCascadeRunResource, 'windowFrom' | 'windowTo'>,
) => {
  const from = DateTime.fromISO(run.windowFrom).plus({ days: 1 });
  const to = DateTime.fromISO(run.windowTo);

  if (from > to) return '-';

  if (from.hasSame(to, 'day')) return from.toFormat('dd/LL/yyyy');

  return `${from.toFormat('dd/LL')} – ${to.toFormat('dd/LL/yyyy')}`;
};

const REASON_LABELS: Record<string, string> = {
  source_post_missing: 'Source post was deleted',
  page_inactive: 'Page not authorised or deleted',
  no_image: 'Source post has no image',
  no_video: 'Source post has no video',
  already_queued: 'Article already in page queue',
  recently_published: 'Article published on page in last 90 days',
};

export const formatSkipReason = (reason: string | null) => {
  if (!reason) return null;

  const [key, postId] = reason.split(':');
  const label = REASON_LABELS[key] ?? reason;

  return postId ? `${label} (#${postId})` : label;
};

type BadgeTone = 'success' | 'error' | 'warning' | 'info' | 'neutral';

const BADGE_COLORS: Record<BadgeTone, [Color, Color]> = {
  success: ['success', 'successLight'],
  error: ['error', 'errorLight'],
  warning: ['warning', 'warningLight'],
  info: ['info', 'infoLight'],
  neutral: ['gray', 'grayscale'],
};

export const Badge = styled(Text).attrs({
  as: 'span',
  $type: 'smallBold',
})<{ $tone: BadgeTone }>`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  white-space: nowrap;
  text-transform: capitalize;
  ${({ theme, $tone }) => css`
    color: ${theme.colors[BADGE_COLORS[$tone][0]]};
    background: ${theme.colors[BADGE_COLORS[$tone][1]]};
  `}
`;

const ITEM_STATUS_TONES: Record<DQCascadeItemStatus, BadgeTone> = {
  pending: 'warning',
  processing: 'warning',
  inserted: 'success',
  skipped: 'neutral',
  failed: 'error',
};

export const ItemStatusBadge = ({
  status,
}: {
  status: DQCascadeItemStatus;
}) => <Badge $tone={ITEM_STATUS_TONES[status]}>{status}</Badge>;

export const RunStatusBadge = ({ run }: { run: DQCascadeRunResource }) =>
  run.status === 'running' ? (
    <Badge $tone="info">Running</Badge>
  ) : run.itemCounts.failed > 0 ? (
    <Badge $tone="error">{run.itemCounts.failed} failed</Badge>
  ) : (
    <Badge $tone="success">Completed</Badge>
  );
